import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import { Users, TrendingUp, Star, GitBranch, ArrowRight, Gauge, Smartphone } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PageTooltip } from '../components/TooltipSystem';
import TierBadge from '../../staff/components/TierBadge';
import { ADMIN_ROUTE_MAP } from '../adminRoutes';
import { useTimelineEvents } from '../components/timeline/useTimelineEvents';
import { buildTimelineDots } from '../components/timeline/buildTimelineDots';
import { TimelineToggleBar } from '../components/timeline/TimelineToggleBar';
import { bucketDateMMDD } from '../components/timeline/timelineUtils';

const DEFAULT_FAVORITES = ['/admin/broadcast', '/admin/coupons', '/admin/challenges', '/admin/gift-points'];

function KpiCard({ label, value, icon: Icon, color, testid, delay }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow duration-300 animate-fade-in-up h-full" style={{ animationDelay: `${delay}s` }} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon size={20} strokeWidth={1.5} style={{ color }} />
        </div>
      </div>
      <div className="text-3xl font-bold text-[#1A1A1A] tabular-nums tracking-tight">{value?.toLocaleString('it-IT') ?? '—'}</div>
      <div className="text-[#64748B] text-sm mt-1">{label}</div>
    </div>
  );
}

function PwaKpiCard({ installed, rate, total, delay }) {
  const color = '#8B5CF6';
  const pct = typeof rate === 'number' ? rate : 0;
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow duration-300 animate-fade-in-up h-full" style={{ animationDelay: `${delay}s` }} data-testid="kpi-pwa-installed">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Smartphone size={20} strokeWidth={1.5} style={{ color }} />
        </div>
        <span className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>{pct}%</span>
      </div>
      <div className="text-3xl font-bold text-[#1A1A1A] tabular-nums tracking-tight">
        {installed?.toLocaleString('it-IT') ?? '—'}
      </div>
      <div className="text-[#64748B] text-sm mt-1">App installata</div>
      {total > 0 && (
        <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
        </div>
      )}
    </div>
  );
}

const LIMIT_LABELS = {
  max_customers: 'Clienti', max_messages_month: 'Messaggi/mese',
  max_automations: 'Automazioni', max_rewards: 'Premi',
  max_venues: 'Punti vendita', max_staff: 'Staff',
};

function UsageBar({ label, current, limit }) {
  if (limit === -1) return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-[#64748B]">{label}</span>
      <span className="text-[10px] text-[#94A3B8]">{current} · Illimitati</span>
    </div>
  );
  const pct = limit > 0 ? Math.min(100, (current / limit) * 100) : 0;
  const color = pct >= 90 ? '#EF4444' : pct >= 70 ? '#F59E0B' : '#10B981';
  return (
    <div className="py-1.5" data-testid={`usage-${label}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[#64748B]">{label}</span>
        <span className="text-[10px] font-semibold tabular-nums" style={{ color }}>{current}/{limit}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(DEFAULT_FAVORITES);
  const [usage, setUsage] = useState(null);
  const [timelineListOpen, setTimelineListOpen] = useState(false);
  const timeline = useTimelineEvents();
  const navigate = useNavigate();

  useEffect(() => {
    adminApi.get('/admin/overview').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    adminApi.get('/admin/favorites').then(r => {
      const favs = r.data.favorites;
      if (favs && favs.length > 0) setFavorites(favs);
    }).catch(() => {});
    adminApi.get('/admin/usage').then(r => setUsage(r.data)).catch(() => {});
  }, []);

  const quickActions = favorites
    .map(route => ADMIN_ROUTE_MAP[route])
    .filter(Boolean);

  const chartData = React.useMemo(() => {
    if (!data?.daily_customers) return [];
    const days = Array.from({ length: 14 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (13 - i));
      return d.toISOString().split('T')[0];
    });
    return days.map(date => ({
      date: date.slice(5),
      count: data.daily_customers.find(d => d.date === date)?.count || 0
    }));
  }, [data]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10">
      <PageTooltip page="overview" />
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1A1A1A]">Overview</h1>
        <p className="text-[#64748B] text-base mt-1">Panoramica del tuo locale</p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 mb-8 md:mb-10">
        <KpiCard label="Clienti totali" value={data?.total_customers} icon={Users} color="#D44E20" testid="kpi-total-customers" delay={0} />
        <KpiCard label="Attivi (30gg)" value={data?.active_customers_30d} icon={TrendingUp} color="#10B981" testid="kpi-active" delay={0.05} />
        <KpiCard label="Punti in circolo" value={data?.points_in_circulation} icon={Star} color="#F59E0B" testid="kpi-points" delay={0.1} />
        <KpiCard label="Referral del mese" value={data?.monthly_referrals} icon={GitBranch} color="#3B82F6" testid="kpi-referrals" delay={0.15} />
        <PwaKpiCard installed={data?.pwa_installed} rate={data?.pwa_install_rate} total={data?.total_customers} delay={0.2} />
      </div>

      {/* Usage widget (only if subscription active) */}
      {usage && !usage.is_trial && usage.usage && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] mb-8 md:mb-10 animate-fade-in-up" style={{ animationDelay: '0.17s' }} data-testid="usage-widget">
          <div className="flex items-center gap-2 mb-3">
            <Gauge size={16} className="text-[#D44E20]" />
            <h3 className="text-sm font-semibold text-[#1A1A1A]">Utilizzo piano</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-1">
            {Object.entries(usage.usage).map(([key, val]) => (
              <UsageBar key={key} label={LIMIT_LABELS[key] || key} current={val.current} limit={val.limit} />
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions — driven by favorites */}
      {quickActions.length > 0 && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] mb-8 md:mb-10 animate-fade-in-up" style={{ animationDelay: '0.18s' }} data-testid="quick-actions-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight">Azioni rapide</h3>
            <p className="text-xs text-[#94A3B8]">
              <Star size={11} className="inline mr-1 text-[#F59E0B]" fill="#F59E0B" />
              dalla sidebar
            </p>
          </div>
          <div className={`grid gap-3 ${quickActions.length <= 2 ? 'grid-cols-1 md:grid-cols-2' : quickActions.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
            {quickActions.map(a => {
              const RouteIcon = a.icon;
              return (
                <button
                  key={a.to}
                  onClick={() => navigate(a.to)}
                  data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-200 group text-left"
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110" style={{ background: `${a.color}12` }}>
                    <RouteIcon size={18} strokeWidth={1.5} style={{ color: a.color }} />
                  </div>
                  <span className="text-sm font-medium text-[#1A1A1A] flex-1 truncate">{a.label}</span>
                  <ArrowRight size={14} className="text-[#94A3B8] group-hover:text-[#64748B] transition-colors flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Chart */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4">Nuovi clienti (ultimi 14 giorni)</h3>
          <TimelineToggleBar
            enabled={timeline.enabled}
            setEnabled={timeline.setEnabled}
            categories={timeline.categories}
            setCategories={timeline.setCategories}
            events={timeline.filtered}
            bucketFn={bucketDateMMDD}
            chartData={chartData}
            xKey="date"
            expanded={timelineListOpen}
            setExpanded={setTimelineListOpen}
          />
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 16, fontSize: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0' }} />
              <Line type="monotone" dataKey="count" stroke="#D44E20" strokeWidth={2.5} dot={{ r: 4, fill: '#D44E20', strokeWidth: 0 }} activeDot={{ r: 6, fill: '#D44E20', strokeWidth: 2, stroke: '#fff' }} name="Nuovi clienti" />
              {buildTimelineDots({
                events: timeline.filtered,
                chartData,
                xKey: 'date',
                yMax: Math.max(...chartData.map(d => d.count || 0), 1) * 1.05,
                bucketFn: bucketDateMMDD,
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top customers */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
          <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4">Top 5 clienti per punti</h3>
          <div className="space-y-3">
            {(data?.top_customers || []).slice(0, 5).map((c, i) => (
              <div key={c.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#F1F5F9] transition-colors group">
                <span className={`text-sm w-6 h-6 rounded-lg flex items-center justify-center font-bold ${i < 3 ? 'bg-[#FDECE7] text-[#D44E20]' : 'bg-slate-100 text-[#64748B]'}`}>{i + 1}</span>
                <div className="w-8 h-8 bg-[#FDECE7] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D44E20] font-bold text-sm">{c.name?.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1A1A1A] text-sm font-medium truncate">{c.name}</p>
                  <TierBadge tier={c.tier} />
                </div>
                <span className="text-[#D44E20] font-bold text-sm tabular-nums">{c.points?.toLocaleString('it-IT')} pt</span>
              </div>
            ))}
          </div>
        </div>

        {/* Last redemptions */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow duration-300 lg:col-span-2 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <h3 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4">Ultimi premi riscattati</h3>
          <div className="divide-y divide-slate-100">
            {(data?.last_redemptions || []).map(r => (
              <div key={r.id} className="flex items-center justify-between py-3 hover:bg-[#F1F5F9]/50 px-2 rounded-xl transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{r.reward_emoji || '🎁'}</span>
                  <div>
                    <p className="text-[#1A1A1A] text-sm font-medium">{r.reward_name}</p>
                    <p className="text-[#94A3B8] text-xs">{r.customer_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[#EF4444] font-semibold text-sm tabular-nums">-{r.points_spent} pt</p>
                  <p className="text-[#94A3B8] text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : ''}</p>
                </div>
              </div>
            ))}
            {!data?.last_redemptions?.length && <p className="text-[#64748B] text-sm text-center py-4">Nessun riscatto ancora</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
